import { LimitedAccessBanner } from '@components/LimitedAccessBanner';
import { getTenantIdentifier } from '@utils/getTenantIdentifier';
import { useGetMe } from '@utils/useGetMe';
import { useResponsive } from '@utils/useResponsive';
import Cookies from 'js-cookie';
import React, { useEffect } from 'react';
import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { LoadingScreen } from '../../../components/LoadingScreen';
import { BuiltInRoles, SubscriptionStatus, useGetLoginInfoQuery, useTenantSubscriptionDetailsQuery, useTenantThemeQuery } from '../../../gql';
import { MobileBottomNavigation, mobileBottomNavigationHeight } from '../components/MobileBottomNavigation';
import { InactiveSubscriptionModal } from '../components/InactiveSubscriptionModal';
import { NavigationMenu } from '../components/NavigationMenu';
import { SettingsDrawer } from '../components/SettingsDrawer';
import { TopBar } from '../components/TopBar/TopBar';
import { TrialEndedModal } from '../components/TrialEndedModal';
import { shouldShowJiraWidget } from '../utils';
import { isProjectNavigationMenuCollapsedState } from '../../projects/atoms';
import { projectCollapsedMenuWidth, projectExpandedMenuWidth } from '../../projects/components/ProjectNavigation';
import { Box, Container } from '@mui/material';

interface Props {
  allowedRoles?: BuiltInRoles[];
}

function initializeJiraHelpDesk() {
  const jhdScript = document.createElement('script');
  jhdScript.type = 'text/javascript';
  jhdScript.setAttribute('data-jsd-embedded', '');
  jhdScript.setAttribute('data-key', 'aee7cf86-f96f-4222-acdd-c16a27a17ae8');
  jhdScript.setAttribute('data-base-url', 'https://jsd-widget.atlassian.com');
  jhdScript.src = 'https://jsd-widget.atlassian.com/assets/embed.js';

  jhdScript.onload = function () {
    const event = new Event('DOMContentLoaded');
    window.document.dispatchEvent(event);
  };

  document.getElementsByTagName('head')[0].appendChild(jhdScript);
}


export const AuthenticatedPageLayout: React.FC<Props> = ({ allowedRoles }) => {
  const { me, isLoading, isFetched, isExternalUser } = useGetMe();
  const { data: loginInfo } = useGetLoginInfoQuery(undefined, { select: d => d.loginInfo });
  const isSignedInWithMagicLink = loginInfo?.isAuthenticatedWithMagicLink;
  const { data: tenantTheme } = useTenantThemeQuery(undefined, { select: d => d.tenantTheme });
  const { isMobile } = useResponsive();
  const { projectId } = useParams();
  const isProjectNavigationCollapsed = useAtomValue(isProjectNavigationMenuCollapsedState);

  const { data: subscriptionDetails } = useTenantSubscriptionDetailsQuery(undefined, { select: d => d.tenantSubscriptionDetails, enabled: me != null });

  // 'paused' status means a trial has ended and no payment method was yet provided
  const isTrialEndedAndPendingPayment = subscriptionDetails?.subscription?.status === SubscriptionStatus.Paused;
  const location = useLocation();

  const isOrganizationMember = !isExternalUser;
  const shouldShowMobileBottomNavigation = isMobile && projectId == null && !isExternalUser;

  const isAuthorized = allowedRoles == null
    ? (me?.builtInRoles.length ?? 0 > 0)
    : me?.builtInRoles.some(r => allowedRoles.includes(r));

  useEffect(() => {
    const isWidgetShown = shouldShowJiraWidget({
      isBrandingEnabled: tenantTheme?.isBrandingEnabled ?? true,
      isExternalUser
    });

    if (!isWidgetShown) return;

    const alreadyInitializedWidget = document.getElementById('jsd-widget');

    if (alreadyInitializedWidget) return;

    initializeJiraHelpDesk();
  }, [isAuthorized, isExternalUser, tenantTheme?.isBrandingEnabled]);

  if (isLoading) {
    return (<LoadingScreen />);
  }

  if (me == null && isFetched) {
    Cookies.remove('tenantIdentifier');
    const identifier = getTenantIdentifier();

    if (identifier) {
      return <Navigate to={`/${identifier}/login?returnUrl=${location.pathname}`} />;
    } else {
      return <Navigate to={`/?returnUrl=${location.pathname}`} />;
    }
  }

  if (me && isAuthorized === false) {
    return <Navigate to={'/' + getTenantIdentifier()} />;
  }


  return <>
    {(isTrialEndedAndPendingPayment && isOrganizationMember) && (
      <TrialEndedModal />
    )}
    {(subscriptionDetails?.subscription != null
      && subscriptionDetails.subscription.status !== SubscriptionStatus.Active
      && subscriptionDetails.subscription.status !== SubscriptionStatus.InTrial
      && isOrganizationMember) && (<InactiveSubscriptionModal subscription={subscriptionDetails.subscription} />)}

    <TopBar />

    {isSignedInWithMagicLink && (!isMobile || !projectId) && (
      <Box
        sx={{
          py: 1,
          ...(projectId && !isMobile && {
            marginLeft: isProjectNavigationCollapsed ? `${projectCollapsedMenuWidth}px` : `${projectExpandedMenuWidth}px`,
            transition: (theme) => theme.transitions.create('margin', {
              easing: theme.transitions.easing.sharp,
              duration: isProjectNavigationCollapsed ? theme.transitions.duration.leavingScreen : theme.transitions.duration.enteringScreen
            })
          })
        }}
      >
        <Container maxWidth="xl" disableGutters sx={{ px: { xs: 2, sm: 3 } }}>
          <LimitedAccessBanner />
        </Container>
      </Box>
    )}

    {!isExternalUser && (
      <NavigationMenu />
    )}

    <SettingsDrawer />

    <main style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto', paddingBottom: shouldShowMobileBottomNavigation ? mobileBottomNavigationHeight : 0 }}>
      <Outlet />
    </main>

    {shouldShowMobileBottomNavigation && (
      <MobileBottomNavigation />
    )}
  </>;
};